import { TableAttachment } from '../../types/attachment';
import { scheduledSendExportFormats } from 'common/downloadLinks';
import { I18n } from 'common/i18n';
import { ATTACHMENT_ICONS } from '../../constants';
import { isStory } from 'common/views/view_types';
import { View } from 'common/types/view';
import { isEmpty, isUndefined } from 'lodash';
import { Hierarchy } from 'common/visualizations/vif';

const scope = 'shared.components.schedule_send_report.modal.attachment_section';

export const getTableOptions = (tableList: TableAttachment[] | void) => {
  const tableAttachmentsList =
    tableList?.map((table: TableAttachment) => {
      return {
        label: table.title,
        value: table.componentUuid,
        leadingIcon: ATTACHMENT_ICONS['table_export'],
        leadingIconType: 'component',
        tooltip: { text: table.title },
      };
    }) ?? [];

  const fullReport = {
    label: I18n.t('full_report', { scope }),
    value: 'full-report',
    leadingIcon: ATTACHMENT_ICONS['full_report'],
    leadingIconType: 'component',
    tooltip: { text: I18n.t('full_report', { scope }) },
  };

  return [fullReport, ...tableAttachmentsList];
};

export const getExportFormatOptions = () => {
  return scheduledSendExportFormats.map((exportFormat) => {
    return {
      label: I18n.t(`shared.explore_grid.export_dataset_modal.${exportFormat}`),
      value: exportFormat,
    };
  });
};

export const sortComponents = (components: any[]) => {
  return components.sort((a, b) => {
    const ax = a.layout?.x;
    const ay = a.layout?.y;
    const bx = b.layout?.x;
    const by = b.layout?.y;

    //Check for invalid layout values
    const aInvalid =
      isUndefined(a.layout) || isUndefined(ax) || isUndefined(ay) || ax === null || ay === null;
    const bInvalid =
      isUndefined(b.layout) || isUndefined(bx) || isUndefined(by) || bx === null || by === null;

    if (aInvalid && !bInvalid) return 1;
    if (!aInvalid && bInvalid) return -1;
    if (aInvalid && bInvalid) return 0;

    //Sort Top -> Bottom
    if (ay !== by) return (ay as number) - (by as number);

    //Sort Left -> Right
    return (ax as number) - (bx as number);
  });
};

export const buildDefaultTableName = (tableCount: number) => {
  return `Table ${tableCount}`;
};

export const tablesFromStoryData = (view: View) => {
  if (isStory(view) && !isUndefined(window.STORY_DATA)) {
    let tableCount = 0;
    const isFlexibleStory = window.STORY_DATA.isFlexibleStory;
    const tablesComponents = window.STORY_DATA.blocks
      .map((block: any) => {
        const components = isFlexibleStory ? sortComponents(block.components) : block.components;

        return components.map((component: any) => {
          if (component.type === 'socrata.visualization.agTable') {
            tableCount = tableCount + 1;
            const title = isEmpty(component.value.vif.title)
              ? buildDefaultTableName(tableCount)
              : component.value.vif.title;
            const hierarchies = component.value.vif.series[0].dataSource.hierarchies as Hierarchy[];
            const isWithGrouping = hierarchies?.some((hierarchy) => !isEmpty(hierarchy.columnConfigurations));

            // there should always be a componentUuid but putting a fallback just in case
            const componentUuid = component.componentUuid || `table-number-${tableCount}`;

            return {
              componentUuid: componentUuid,
              title: title,
              withGrouping: isWithGrouping === undefined ? false : isWithGrouping,
              withMultipleHierarchy: hierarchies?.length > 1,
            };
          }
        });
      })
      .flat()
      .filter((tableObj: TableAttachment) => tableObj !== undefined) as TableAttachment[];

    return tablesComponents;
  }
};

export const generateDefaultPdfConfig = (isTableWithGrouping: boolean) => {
  return {
    pageSize: 'Letter',
    orientation: 'landscape',
    columnWidth: 'bestfit',
    textOverflow: 'linebreak',
    ...(isTableWithGrouping && { pageBreak: 'standard' }),
  };
};

export const joinTableNames = (tables: string[]) => {
  if (tables.length === 0) return '';
  if (tables.length === 1) return tables[0];
  if (tables.length === 2) return tables.join(` ${I18n.t('and', { scope })} `);
  return tables.slice(0, -1).join(', ') + `, ${I18n.t('and', { scope })} ` + tables[tables.length - 1];
};
